import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../config/api";
import { appendAuthToken } from "../utils/auth";

const emptyItem = () => ({
  productId: "",
  itemCode: "",
  itemName: "",
  hsnCode: "",
  quantity: "",
  unit: "No",
  rate: "",
  amount: "",
  taxPercent: "",
  taxAmount: ""
});

const todayInput = () => new Date().toISOString().slice(0, 10);

const createEmptyForm = (noteType = "debit") => ({
  noteType,
  status: "draft",
  noteNumber: "",
  noteDate: todayInput(),
  financialYear: "",
  branchName: "",
  counterpartyType: noteType === "debit" ? "supplier" : "customer",
  dealerId: "",
  customerId: "",
  counterpartyName: "",
  counterpartyGst: "",
  counterpartyPhone: "",
  counterpartyEmail: "",
  counterpartyAddress: "",
  referenceInvoiceId: "",
  referenceInvoiceNumber: "",
  referenceInvoiceType: noteType === "debit" ? "purchase" : "sale",
  referenceInvoiceDate: "",
  reason: "",
  notes: "",
  additionalCharges: "",
  deductions: "",
  roundOffAmount: "",
  gstType: "cgst_sgst",
  cgstPercent: "9",
  sgstPercent: "9",
  igstPercent: "18",
  items: [emptyItem()]
});

const toDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatCurrency = (value) => Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const financialYearFromDate = (dateValue) => {
  const date = new Date(dateValue || new Date());
  const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
};

const groupInvoices = (rows = []) => {
  const groups = new Map();
  rows.forEach((row) => {
    const docNo = String(row.documentNumber || "").trim();
    const key = docNo
      ? `${row.entryType || "entry"}::doc::${docNo.toLowerCase()}`
      : String(row.documentGroupKey || row._id || "");
    if (!groups.has(key)) {
      groups.set(key, { ...row, _groupKey: key, _rows: [row] });
      return;
    }
    const group = groups.get(key);
    if (!group._rows.some((item) => String(item._id) === String(row._id))) {
      group._rows.push(row);
    }
  });
  return Array.from(groups.values()).sort((left, right) => new Date(right.transactionDate || right.createdAt || 0) - new Date(left.transactionDate || left.createdAt || 0));
};

const calculateItemTotals = (item, gstType, cgstPercent, sgstPercent, igstPercent) => {
  const quantity = Number(item.quantity || 0);
  const rate = Number(item.rate || 0);
  const amount = Number((quantity * rate).toFixed(2));
  const taxPercent = Number(item.taxPercent || (gstType === "igst" ? igstPercent : Number(cgstPercent || 0) + Number(sgstPercent || 0)) || 0);
  const taxAmount = Number((amount * taxPercent / 100).toFixed(2));
  return {
    ...item,
    quantity: String(item.quantity ?? ""),
    rate: String(item.rate ?? ""),
    amount: String(amount),
    taxPercent: String(taxPercent),
    taxAmount: String(taxAmount)
  };
};

const calculateFormTotals = (form) => {
  const items = (form.items || []).map((item) => calculateItemTotals(
    item,
    form.gstType,
    form.cgstPercent,
    form.sgstPercent,
    form.igstPercent
  ));
  const subTotal = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const taxAmount = items.reduce((sum, item) => sum + Number(item.taxAmount || 0), 0);
  const additionalCharges = Number(form.additionalCharges || 0);
  const deductions = Number(form.deductions || 0);
  const roundOffAmount = Number(form.roundOffAmount || 0);
  const taxableAmount = Number((subTotal + additionalCharges - deductions + roundOffAmount).toFixed(2));
  const totalAmount = Number((taxableAmount + taxAmount).toFixed(2));
  const cgstAmount = form.gstType === "igst" ? 0 : Number((taxableAmount * Number(form.cgstPercent || 0) / 100).toFixed(2));
  const sgstAmount = form.gstType === "igst" ? 0 : Number((taxableAmount * Number(form.sgstPercent || 0) / 100).toFixed(2));
  const igstAmount = form.gstType === "igst" ? Number((taxableAmount * Number(form.igstPercent || 0) / 100).toFixed(2)) : 0;

  return {
    items,
    subTotal,
    taxAmount,
    taxableAmount,
    totalAmount,
    cgstAmount,
    sgstAmount,
    igstAmount
  };
};

function StatCard({ label, value, hint, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50",
    emerald: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    sky: "border-sky-200 bg-sky-50"
  };

  return (
    <div className={`rounded-[1.5rem] border p-4 ${tones[tone] || tones.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-3 text-2xl font-bold text-slate-950">{value}</p>
      {hint ? <p className="mt-2 text-xs text-slate-600">{hint}</p> : null}
    </div>
  );
}

function Input({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

function SectionTitle({ title, action }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-lg font-bold text-slate-950 sm:text-xl">{title}</h2>
      </div>
      {action}
    </div>
  );
}

const fieldClass = "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-4 focus:ring-teal-100";

export default function AccountingNotes() {
  const [noteType, setNoteType] = useState("debit");
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [partyFilter, setPartyFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportsTab, setReportsTab] = useState("register");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(createEmptyForm("debit"));
  const [dealers, setDealers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [reportSummary, setReportSummary] = useState({ debitCount: 0, creditCount: 0, totalAmount: 0, taxAmount: 0 });
  const [reportLoading, setReportLoading] = useState(false);

  const selectedPartyList = noteType === "debit" ? dealers : customers;
  const noteTitle = noteType === "debit" ? "Debit Note" : "Credit Note";
  const referenceInvoiceType = noteType === "debit" ? "purchase" : "sale";

  const loadMasters = useCallback(async () => {
    const [dealersRes, customersRes] = await Promise.allSettled([
      axios.get(API_BASE_URL + "/api/dealers"),
      axios.get(API_BASE_URL + "/api/customers")
    ]);
    setDealers(Array.isArray(dealersRes.value?.data) ? dealersRes.value.data : []);
    setCustomers(Array.isArray(customersRes.value?.data) ? customersRes.value.data : []);
  }, []);

  const loadInvoices = useCallback(async (type = noteType) => {
    const res = await axios.get(API_BASE_URL + "/api/account-documents", {
      params: { entryType: type === "debit" ? "purchase" : "sale" }
    });
    setInvoices(groupInvoices(Array.isArray(res.data) ? res.data : []));
  }, [noteType]);

  const loadNotes = useCallback(async (type = noteType) => {
    setLoadingNotes(true);
    try {
      const params = {
        noteType: type,
        search,
        status: statusFilter !== "all" ? statusFilter : "",
        counterpartyType: partyFilter !== "all" ? partyFilter : "",
        fromDate,
        toDate
      };
      const response = await axios.get(API_BASE_URL + "/api/accounting-notes", { params });
      setNotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load notes", error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  }, [noteType, search, statusFilter, partyFilter, fromDate, toDate]);

  const loadReports = useCallback(async () => {
    setReportLoading(true);
    try {
      const response = await axios.get(API_BASE_URL + "/api/accounting-notes/reports", {
        params: {
          reportType: reportsTab,
          noteType,
          search,
          fromDate,
          toDate
        }
      });
      if (reportsTab === "summary") {
        setReportSummary(response.data?.summary || { debitCount: 0, creditCount: 0, totalAmount: 0, taxAmount: 0 });
        setReportRows(response.data?.notes || []);
      } else if (response.data?.notes) {
        setReportSummary(response.data?.summary || { debitCount: 0, creditCount: 0, totalAmount: 0, taxAmount: 0 });
        setReportRows(response.data.notes || []);
      } else if (Array.isArray(response.data)) {
        setReportRows(response.data);
      } else {
        setReportRows([]);
      }
    } catch (error) {
      console.error("Failed to load reports", error);
      setReportRows([]);
    } finally {
      setReportLoading(false);
    }
  }, [reportsTab, noteType, search, fromDate, toDate]);

  useEffect(() => {
    loadMasters().catch((error) => console.error("Failed to load master data", error));
  }, [loadMasters]);

  useEffect(() => {
    loadInvoices(noteType).catch((error) => console.error("Failed to load invoices", error));
    if (!editingId) {
      setForm(createEmptyForm(noteType));
    }
  }, [noteType, editingId, loadInvoices]);

  useEffect(() => {
    loadNotes(noteType).catch((error) => console.error("Failed to load notes", error));
  }, [noteType, search, statusFilter, partyFilter, fromDate, toDate, loadNotes]);

  useEffect(() => {
    loadReports().catch((error) => console.error("Failed to load reports", error));
  }, [reportsTab, noteType, search, fromDate, toDate, loadReports]);

  const filteredPartyOptions = useMemo(() => selectedPartyList, [selectedPartyList]);

  const selectedInvoiceOptions = useMemo(() => invoices.map((invoice) => ({
    value: invoice._groupKey || invoice._id,
    label: `${invoice.documentNumber || "-"} • ${invoice.counterpartyName || invoice.customerName || invoice.supplierName || "-"} • ${formatDate(invoice.transactionDate)}`
  })), [invoices]);

  const findPartyIdByName = (partyName = "") => {
    const normalized = String(partyName || "").trim().toLowerCase();
    if (!normalized) return "";
    const list = noteType === "debit" ? dealers : customers;
    const match = list.find((party) => [
      party.companyName,
      party.name,
      party.customerId,
      party.dealerId
    ].filter(Boolean).some((value) => String(value).trim().toLowerCase() === normalized));
    return match?._id || "";
  };

  const currentTotals = useMemo(() => calculateFormTotals(form), [form]);

  const updateFormField = (name, value) => {
    setForm((current) => {
      if (name === "noteDate") {
        return {
          ...current,
          noteDate: value,
          financialYear: financialYearFromDate(value)
        };
      }

      if (name === "counterpartyType") {
        return {
          ...current,
          counterpartyType: value,
          dealerId: value === "supplier" ? current.dealerId : "",
          customerId: value === "customer" ? current.customerId : ""
        };
      }

      if (name === "gstType") {
        const next = {
          ...current,
          gstType: value,
          cgstPercent: value === "igst" ? "0" : current.cgstPercent || "9",
          sgstPercent: value === "igst" ? "0" : current.sgstPercent || "9",
          igstPercent: value === "igst" ? current.igstPercent || "18" : "0"
        };
        next.items = next.items.map((item) => calculateItemTotals(item, value, next.cgstPercent, next.sgstPercent, next.igstPercent));
        return next;
      }

      if (["cgstPercent", "sgstPercent", "igstPercent"].includes(name)) {
        const next = {
          ...current,
          [name]: value
        };
        next.items = next.items.map((item) => calculateItemTotals(item, next.gstType, next.cgstPercent, next.sgstPercent, next.igstPercent));
        return next;
      }

      return {
        ...current,
        [name]: value
      };
    });
  };

  const updateItemField = (index, name, value) => {
    setForm((current) => {
      const items = current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const nextItem = { ...item, [name]: value };
        if (name === "quantity" || name === "rate" || name === "taxPercent") {
          const calculated = calculateItemTotals(nextItem, current.gstType, current.cgstPercent, current.sgstPercent, current.igstPercent);
          return calculated;
        }
        return nextItem;
      });
      return {
        ...current,
        items
      };
    });
  };

  const addItem = () => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }));
  const removeItem = (index) => setForm((current) => ({
    ...current,
    items: current.items.length === 1 ? current.items : current.items.filter((_, itemIndex) => itemIndex !== index)
  }));

  const applyPartySelection = (partyId) => {
    const party = selectedPartyList.find((entry) => String(entry._id) === String(partyId));
    if (!party) return;

    setForm((current) => {
      const next = {
        ...current,
        dealerId: noteType === "debit" ? party._id : "",
        customerId: noteType === "credit" ? party._id : "",
        counterpartyName: party.companyName || party.name || current.counterpartyName,
        counterpartyGst: party.gstNumber || current.counterpartyGst,
        counterpartyPhone: party.phone || current.counterpartyPhone,
        counterpartyEmail: party.email || current.counterpartyEmail,
        counterpartyAddress: [
          party.address?.street,
          party.address?.area,
          party.address?.city,
          party.address?.state,
          party.address?.pincode
        ].filter(Boolean).join(", ") || current.counterpartyAddress
      };
      return next;
    });
  };

  const applyInvoiceSelection = (invoiceId) => {
    const invoice = invoices.find((entry) => String(entry._groupKey || entry._id) === String(invoiceId));
    if (!invoice) return;

    setForm((current) => {
    const next = {
      ...current,
      referenceInvoiceId: invoice._groupKey || invoice._id,
      referenceInvoiceNumber: invoice.documentNumber || "",
      referenceInvoiceDate: toDateInput(invoice.transactionDate),
      referenceInvoiceType,
      counterpartyName: invoice.counterpartyName || current.counterpartyName,
      counterpartyGst: invoice.counterpartyGst || current.counterpartyGst,
      counterpartyPhone: invoice.counterpartyPhone || current.counterpartyPhone,
      counterpartyEmail: invoice.counterpartyEmail || current.counterpartyEmail,
      counterpartyAddress: invoice.counterpartyAddress || current.counterpartyAddress,
      dealerId: noteType === "debit" ? (findPartyIdByName(invoice.counterpartyName) || current.dealerId) : "",
      customerId: noteType === "credit" ? (findPartyIdByName(invoice.counterpartyName) || current.customerId) : ""
    };
      return next;
    });
  };

  const loadNoteIntoForm = (note) => {
    setEditingId(note._id);
    setNoteType(note.noteType || "debit");
    setForm({
      noteType: note.noteType || "debit",
      status: note.status || "draft",
      noteNumber: note.noteNumber || "",
      noteDate: toDateInput(note.noteDate),
      financialYear: note.financialYear || "",
      branchName: note.branchName || "",
      counterpartyType: note.counterpartyType || (note.noteType === "debit" ? "supplier" : "customer"),
      dealerId: note.dealerId || "",
      customerId: note.customerId || "",
      counterpartyName: note.counterpartyName || "",
      counterpartyGst: note.counterpartyGst || "",
      counterpartyPhone: note.counterpartyPhone || "",
      counterpartyEmail: note.counterpartyEmail || "",
      counterpartyAddress: note.counterpartyAddress || "",
      referenceInvoiceId: note.referenceInvoiceId || "",
      referenceInvoiceNumber: note.referenceInvoiceNumber || "",
      referenceInvoiceType: note.referenceInvoiceType || (note.noteType === "debit" ? "purchase" : "sale"),
      referenceInvoiceDate: toDateInput(note.referenceInvoiceDate),
      reason: note.reason || "",
      notes: note.notes || "",
      additionalCharges: String(note.additionalCharges ?? ""),
      deductions: String(note.deductions ?? ""),
      roundOffAmount: String(note.roundOffAmount ?? ""),
      gstType: note.gstType || "cgst_sgst",
      cgstPercent: String(note.cgstPercent ?? 9),
      sgstPercent: String(note.sgstPercent ?? 9),
      igstPercent: String(note.igstPercent ?? 18),
      items: Array.isArray(note.items) && note.items.length ? note.items.map((item) => ({
        ...emptyItem(),
        ...item,
        quantity: String(item.quantity ?? ""),
        rate: String(item.rate ?? ""),
        amount: String(item.amount ?? ""),
        taxPercent: String(item.taxPercent ?? ""),
        taxAmount: String(item.taxAmount ?? "")
      })) : [emptyItem()]
    });
  };

  const resetForm = (nextType = noteType) => {
    setEditingId("");
    setForm(createEmptyForm(nextType));
  };

  const saveNote = async (nextStatus = "draft") => {
    try {
      setSaving(true);
      const payload = {
        ...form,
        noteType,
        status: nextStatus,
        items: currentTotals.items,
        noteDate: form.noteDate,
        financialYear: form.financialYear || financialYearFromDate(form.noteDate),
        subTotal: currentTotals.subTotal,
        taxableAmount: currentTotals.taxableAmount,
        taxAmount: currentTotals.taxAmount,
        totalAmount: currentTotals.totalAmount
      };
      if (editingId) {
        await axios.put(API_BASE_URL + `/api/accounting-notes/${editingId}`, payload);
      } else {
        await axios.post(API_BASE_URL + "/api/accounting-notes", payload);
      }
      resetForm(noteType);
      await loadNotes(noteType);
      await loadReports();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const approveNote = async (noteId) => {
    try {
      setSaving(true);
      await axios.post(API_BASE_URL + `/api/accounting-notes/${noteId}/approve`);
      await loadNotes(noteType);
      await loadReports();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to approve note");
    } finally {
      setSaving(false);
    }
  };

  const cancelNote = async (noteId) => {
    if (!window.confirm("Cancel this note? This will reverse its accounting impact.")) return;
    try {
      setSaving(true);
      await axios.post(API_BASE_URL + `/api/accounting-notes/${noteId}/cancel`);
      await loadNotes(noteType);
      await loadReports();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to cancel note");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note permanently?")) return;
    try {
      setSaving(true);
      await axios.delete(API_BASE_URL + `/api/accounting-notes/${noteId}`);
      await loadNotes(noteType);
      await loadReports();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete note");
    } finally {
      setSaving(false);
    }
  };

  const openPdf = (note, download = false) => {
    const query = download ? "?download=1" : "";
    window.open(appendAuthToken(API_BASE_URL + `/api/accounting-notes/${note._id}/pdf${query}`), "_blank", "noopener,noreferrer");
  };

  const shareEmail = (note) => {
    const subject = encodeURIComponent(`${note.noteType === "credit" ? "Credit" : "Debit"} Note ${note.noteNumber}`);
    const pdfLink = appendAuthToken(API_BASE_URL + `/api/accounting-notes/${note._id}/pdf`);
    const body = encodeURIComponent([
      `${note.noteType === "credit" ? "Credit" : "Debit"} Note`,
      `Number: ${note.noteNumber}`,
      `Date: ${formatDate(note.noteDate)}`,
      `Party: ${note.counterpartyName}`,
      `Total: Rs ${formatCurrency(note.totalAmount)}`,
      `Reference Invoice: ${note.referenceInvoiceNumber || "-"}`,
      "",
      `PDF: ${pdfLink}`
    ].join("\n"));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareWhatsApp = (note) => {
    const text = encodeURIComponent(
      `${note.noteType === "credit" ? "Credit" : "Debit"} Note ${note.noteNumber}\n` +
      `Party: ${note.counterpartyName}\n` +
      `Date: ${formatDate(note.noteDate)}\n` +
      `Amount: Rs ${formatCurrency(note.totalAmount)}\n` +
      `Reference: ${note.referenceInvoiceNumber || "-"}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const exportExcel = () => {
    const rows = notes.map((note) => ({
      "Note No": note.noteNumber,
      "Type": note.noteType,
      "Date": formatDate(note.noteDate),
      "Party": note.counterpartyName,
      "GSTIN": note.counterpartyGst,
      "Invoice": note.referenceInvoiceNumber,
      "Status": note.status,
      "Total": Number(note.totalAmount || 0)
    }));
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, sheet, `${noteTitle} Register`);
    XLSX.writeFile(workbook, `${noteTitle.toLowerCase().replace(/\s+/g, "-")}-register.xlsx`);
  };

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter((note) => {
      if (statusFilter !== "all" && note.status !== statusFilter) return false;
      if (partyFilter !== "all" && note.counterpartyType !== partyFilter) return false;
      if (!q) return true;
      return [
        note.noteNumber,
        note.counterpartyName,
        note.counterpartyGst,
        note.referenceInvoiceNumber,
        note.reason,
        note.notes
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(q));
    });
  }, [notes, partyFilter, search, statusFilter]);

  const reportData = useMemo(() => {
    if (reportsTab === "summary") return notes;
    return reportRows;
  }, [notes, reportRows, reportsTab]);

  const summaryCards = useMemo(() => {
    const totalAmount = filteredNotes.reduce((sum, note) => sum + Number(note.totalAmount || 0), 0);
    const taxAmount = filteredNotes.reduce((sum, note) => sum + Number(note.taxAmount || 0), 0);
    return {
      count: filteredNotes.length,
      approved: filteredNotes.filter((note) => note.status === "approved").length,
      cancelled: filteredNotes.filter((note) => note.status === "cancelled").length,
      totalAmount,
      taxAmount
    };
  }, [filteredNotes]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/40 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-700">Accounting Notes</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Debit Note and Credit Note module
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                Create GST-ready debit notes for supplier adjustments and credit notes for customer adjustments, with print, PDF, audit trail, stock impact, and outstanding balance updates.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:w-[44rem]">
              <StatCard label="Notes" value={String(summaryCards.count)} tone="slate" />
              <StatCard label="Approved" value={String(summaryCards.approved)} tone="emerald" />
              <StatCard label="Cancelled" value={String(summaryCards.cancelled)} tone="amber" />
              <StatCard label="Total" value={`Rs ${formatCurrency(summaryCards.totalAmount)}`} tone="sky" />
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <SectionTitle
              title="Module Mode"
              action={
                <div className="inline-flex rounded-full bg-slate-100 p-1">
                  {[
                    ["debit", "Debit Note"],
                    ["credit", "Credit Note"]
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setEditingId("");
                        setForm(createEmptyForm(value));
                        setNoteType(value);
                      }}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition ${noteType === value ? "bg-teal-600 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              }
            />
            <p className="mt-3 text-sm text-slate-600">
              Working on a {noteTitle.toLowerCase()} for {noteType === "debit" ? "supplier returns and claims" : "customer returns and post-invoice discounts"}.
            </p>
          </div>
          <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <SectionTitle
              title="Filters and Export"
              action={
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={exportExcel} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Export Excel</button>
                  <button type="button" onClick={() => window.open(appendAuthToken(API_BASE_URL + `/api/accounting-notes/export?noteType=${noteType}`), "_blank", "noopener,noreferrer")} className="rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">Download API Export</button>
                </div>
              }
            />
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search note, party, invoice..." className={fieldClass} />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={fieldClass}>
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="saved">Saved</option>
                <option value="approved">Approved</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)} className={fieldClass}>
                <option value="all">All Parties</option>
                <option value="supplier">Supplier</option>
                <option value="customer">Customer</option>
              </select>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={fieldClass} />
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={fieldClass} />
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <form
              className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm"
              onSubmit={(event) => {
                event.preventDefault();
                saveNote("saved");
              }}
            >
              <SectionTitle
                title={`${editingId ? "Edit" : "Create"} ${noteTitle}`}
                action={
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => resetForm(noteType)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Reset</button>
                    <button type="button" onClick={() => saveNote("draft")} disabled={saving} className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-200 disabled:opacity-60">Save Draft</button>
                    <button type="submit" disabled={saving} className="rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
                    <button type="button" onClick={() => saveNote("approved")} disabled={saving} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">Save & Approve</button>
                  </div>
                }
              />

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Input label="Note Date">
                  <input type="date" value={form.noteDate} onChange={(e) => updateFormField("noteDate", e.target.value)} className={fieldClass} />
                </Input>
                <Input label="Financial Year">
                  <input value={form.financialYear || financialYearFromDate(form.noteDate)} readOnly className={`${fieldClass} bg-slate-50`} />
                </Input>
                <Input label="Branch">
                  <input value={form.branchName} onChange={(e) => updateFormField("branchName", e.target.value)} className={fieldClass} placeholder="Branch name" />
                </Input>
                <Input label="Party Type">
                  <select value={form.counterpartyType} onChange={(e) => updateFormField("counterpartyType", e.target.value)} className={fieldClass}>
                    <option value="supplier">Supplier</option>
                    <option value="customer">Customer</option>
                  </select>
                </Input>
                <Input label={noteType === "debit" ? "Supplier" : "Customer"}>
                  <select value={form.counterpartyType === "supplier" ? form.dealerId : form.customerId} onChange={(e) => applyPartySelection(e.target.value)} className={fieldClass}>
                    <option value="">Select party</option>
                    {filteredPartyOptions.map((party) => (
                      <option key={party._id} value={party._id}>
                        {party.companyName || party.name || party.customerId || party.dealerId}
                      </option>
                    ))}
                  </select>
                </Input>
                <Input label="GSTIN">
                  <input value={form.counterpartyGst} onChange={(e) => updateFormField("counterpartyGst", e.target.value)} className={fieldClass} placeholder="Party GSTIN" />
                </Input>
                <Input label="Phone">
                  <input value={form.counterpartyPhone} onChange={(e) => updateFormField("counterpartyPhone", e.target.value)} className={fieldClass} placeholder="Phone" />
                </Input>
                <Input label="Email">
                  <input value={form.counterpartyEmail} onChange={(e) => updateFormField("counterpartyEmail", e.target.value)} className={fieldClass} placeholder="Email" />
                </Input>
                <Input label="Reference Invoice">
                  <select value={form.referenceInvoiceId} onChange={(e) => applyInvoiceSelection(e.target.value)} className={fieldClass}>
                    <option value="">Select invoice</option>
                    {selectedInvoiceOptions.map((invoice) => (
                      <option key={invoice.value} value={invoice.value}>{invoice.label}</option>
                    ))}
                  </select>
                </Input>
                <Input label="Reference Date">
                  <input value={form.referenceInvoiceDate} readOnly className={`${fieldClass} bg-slate-50`} />
                </Input>
                <Input label="Reference No">
                  <input value={form.referenceInvoiceNumber} onChange={(e) => updateFormField("referenceInvoiceNumber", e.target.value)} className={fieldClass} placeholder="Invoice no" />
                </Input>
                <Input label="GST Type">
                  <select value={form.gstType} onChange={(e) => updateFormField("gstType", e.target.value)} className={fieldClass}>
                    <option value="cgst_sgst">CGST + SGST</option>
                    <option value="igst">IGST</option>
                  </select>
                </Input>
                {form.gstType === "igst" ? (
                  <Input label="IGST %">
                    <input type="number" min="0" step="0.01" value={form.igstPercent} onChange={(e) => updateFormField("igstPercent", e.target.value)} className={fieldClass} />
                  </Input>
                ) : (
                  <>
                    <Input label="CGST %">
                      <input type="number" min="0" step="0.01" value={form.cgstPercent} onChange={(e) => updateFormField("cgstPercent", e.target.value)} className={fieldClass} />
                    </Input>
                    <Input label="SGST %">
                      <input type="number" min="0" step="0.01" value={form.sgstPercent} onChange={(e) => updateFormField("sgstPercent", e.target.value)} className={fieldClass} />
                    </Input>
                  </>
                )}
                <Input label="Reason">
                  <input value={form.reason} onChange={(e) => updateFormField("reason", e.target.value)} className={fieldClass} placeholder="Reason for note" />
                </Input>
                <Input label="Additional Charges">
                  <input type="number" min="0" step="0.01" value={form.additionalCharges} onChange={(e) => updateFormField("additionalCharges", e.target.value)} className={fieldClass} />
                </Input>
                <Input label="Deductions">
                  <input type="number" min="0" step="0.01" value={form.deductions} onChange={(e) => updateFormField("deductions", e.target.value)} className={fieldClass} />
                </Input>
                <Input label="Round Off">
                  <input type="number" step="0.01" value={form.roundOffAmount} onChange={(e) => updateFormField("roundOffAmount", e.target.value)} className={fieldClass} />
                </Input>
              </div>

              <div className="mt-6 overflow-x-auto rounded-[1.5rem] border border-slate-200">
                <table className="w-full min-w-[980px] text-left text-sm">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Item</th>
                      <th className="px-4 py-3 font-semibold">HSN</th>
                      <th className="px-4 py-3 font-semibold">Qty</th>
                      <th className="px-4 py-3 font-semibold">Unit</th>
                      <th className="px-4 py-3 font-semibold">Rate</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Tax %</th>
                      <th className="px-4 py-3 font-semibold">Tax Amount</th>
                      <th className="px-4 py-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, index) => (
                      <tr key={`note-item-${index}`} className="border-t border-slate-200">
                        <td className="px-3 py-3"><input value={item.itemName} onChange={(e) => updateItemField(index, "itemName", e.target.value)} className={fieldClass} placeholder="Item name" /></td>
                        <td className="px-3 py-3"><input value={item.hsnCode} onChange={(e) => updateItemField(index, "hsnCode", e.target.value)} className={fieldClass} placeholder="HSN" /></td>
                        <td className="px-3 py-3"><input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateItemField(index, "quantity", e.target.value)} className={fieldClass} placeholder="Qty" /></td>
                        <td className="px-3 py-3"><input value={item.unit} onChange={(e) => updateItemField(index, "unit", e.target.value)} className={fieldClass} placeholder="Unit" /></td>
                        <td className="px-3 py-3"><input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => updateItemField(index, "rate", e.target.value)} className={fieldClass} placeholder="Rate" /></td>
                        <td className="px-3 py-3"><input value={item.amount} readOnly className={`${fieldClass} bg-slate-50`} /></td>
                        <td className="px-3 py-3"><input type="number" min="0" step="0.01" value={item.taxPercent} onChange={(e) => updateItemField(index, "taxPercent", e.target.value)} className={fieldClass} placeholder="Tax %" /></td>
                        <td className="px-3 py-3"><input value={item.taxAmount} readOnly className={`${fieldClass} bg-slate-50`} /></td>
                        <td className="px-3 py-3">
                          <button type="button" onClick={() => removeItem(index)} className="rounded-2xl bg-rose-100 px-4 py-3 text-sm font-semibold text-rose-700 hover:bg-rose-200">
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={addItem} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  + Add Item
                </button>
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subtotal</p>
                    <p className="mt-1 font-bold text-slate-900">Rs {formatCurrency(currentTotals.subTotal)}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tax</p>
                    <p className="mt-1 font-bold text-slate-900">Rs {formatCurrency(currentTotals.taxAmount)}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total</p>
                    <p className="mt-1 font-bold text-slate-900">Rs {formatCurrency(currentTotals.totalAmount)}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">GST Type</p>
                    <p className="mt-1 font-bold text-slate-900">{form.gstType === "igst" ? `IGST ${form.igstPercent}%` : `CGST ${form.cgstPercent}% + SGST ${form.sgstPercent}%`}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <Input label="Notes / Remarks">
                  <textarea value={form.notes} onChange={(e) => updateFormField("notes", e.target.value)} rows={4} className={fieldClass} placeholder="Internal notes or remarks" />
                </Input>
                <Input label="Document Preview">
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Auto-numbering, audit trail, PDF rendering, and inventory/ledger impact will be applied when the note is approved.
                  </div>
                </Input>
              </div>
            </form>

            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionTitle title={`${noteTitle} Register`} />
              <div className="mt-4 overflow-x-auto rounded-[1.5rem] border border-slate-200">
                <table className="w-full min-w-[1100px] text-left text-sm">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="px-4 py-3 font-semibold">No</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Party</th>
                      <th className="px-4 py-3 font-semibold">Reference</th>
                      <th className="px-4 py-3 font-semibold">Reason</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingNotes ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">Loading {noteTitle.toLowerCase()}s...</td>
                      </tr>
                    ) : filteredNotes.length ? (
                      filteredNotes.map((note) => (
                        <tr key={note._id} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-semibold text-slate-900">{note.noteNumber}</td>
                          <td className="px-4 py-3 text-slate-700">{formatDate(note.noteDate)}</td>
                          <td className="px-4 py-3 text-slate-700">{note.counterpartyName || "-"}</td>
                          <td className="px-4 py-3 text-slate-700">{note.referenceInvoiceNumber || "-"}</td>
                          <td className="px-4 py-3 text-slate-700">{note.reason || "-"}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${note.status === "approved" ? "bg-emerald-100 text-emerald-700" : note.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>
                              {note.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900">Rs {formatCurrency(note.totalAmount)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button type="button" onClick={() => openPdf(note)} className="rounded-full bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">Print</button>
                              <button type="button" onClick={() => openPdf(note, true)} className="rounded-full bg-teal-600 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-700">PDF</button>
                              <button type="button" onClick={() => loadNoteIntoForm(note)} className="rounded-full bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700">Edit</button>
                              <button type="button" onClick={() => approveNote(note._id)} disabled={note.status === "approved"} className="rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300">Approve</button>
                              <button type="button" onClick={() => cancelNote(note._id)} disabled={note.status === "cancelled"} className="rounded-full bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-amber-300">Cancel</button>
                              <button type="button" onClick={() => deleteNote(note._id)} className="rounded-full bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700">Delete</button>
                              <button type="button" onClick={() => shareEmail(note)} className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200">Email</button>
                              <button type="button" onClick={() => shareWhatsApp(note)} className="rounded-full bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700">WhatsApp</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">No {noteTitle.toLowerCase()} records found for the current filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionTitle
                title="Reports"
                action={
                  <div className="inline-flex rounded-full bg-slate-100 p-1">
                    {[
                      ["register", "Register"],
                      ["summary", "Summary"],
                      ["party", "Party-wise"],
                      ["gst", "GST"],
                      ["outstanding", "Outstanding"]
                    ].map(([key, label]) => (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setReportsTab(key)}
                        className={`rounded-full px-3 py-2 text-xs font-semibold transition ${reportsTab === key ? "bg-teal-600 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                }
              />
              <div className="mt-4 grid grid-cols-2 gap-3">
                <StatCard label="Rows" value={String(reportData.length || 0)} tone="slate" />
                <StatCard label="Tax" value={`Rs ${formatCurrency(reportSummary.taxAmount || reportData.reduce((sum, row) => sum + Number(row.taxAmount || 0), 0))}`} tone="emerald" />
                <StatCard label="Amount" value={`Rs ${formatCurrency(reportSummary.totalAmount || reportData.reduce((sum, row) => sum + Number(row.totalAmount || 0), 0))}`} tone="sky" />
                <StatCard label="Notes" value={reportsTab === "party" ? "Grouped" : noteTitle} tone="amber" />
              </div>

              <div className="mt-5 overflow-x-auto rounded-[1.5rem] border border-slate-200">
                {reportLoading ? (
                  <div className="px-4 py-10 text-center text-slate-500">Loading report data...</div>
                ) : reportsTab === "summary" ? (
                  <table className="w-full min-w-[820px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Metric</th>
                        <th className="px-4 py-3 font-semibold">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-200"><td className="px-4 py-3">Debit Notes</td><td className="px-4 py-3 font-semibold">{reportSummary.debitCount || 0}</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-3">Credit Notes</td><td className="px-4 py-3 font-semibold">{reportSummary.creditCount || 0}</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-3">Total Value</td><td className="px-4 py-3 font-semibold">Rs {formatCurrency(reportSummary.totalAmount || 0)}</td></tr>
                      <tr className="border-t border-slate-200"><td className="px-4 py-3">GST Value</td><td className="px-4 py-3 font-semibold">Rs {formatCurrency(reportSummary.taxAmount || 0)}</td></tr>
                    </tbody>
                  </table>
                ) : reportData.length ? (
                  <table className="w-full min-w-[820px] text-left text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        {reportsTab === "party" ? (
                          <>
                            <th className="px-4 py-3 font-semibold">Party</th>
                            <th className="px-4 py-3 font-semibold">Type</th>
                            <th className="px-4 py-3 font-semibold">Count</th>
                            <th className="px-4 py-3 font-semibold">Amount</th>
                          </>
                        ) : reportsTab === "outstanding" ? (
                          <>
                            <th className="px-4 py-3 font-semibold">Note No</th>
                            <th className="px-4 py-3 font-semibold">Party</th>
                            <th className="px-4 py-3 font-semibold">Invoice</th>
                            <th className="px-4 py-3 font-semibold">Adjustment</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                          </>
                        ) : (
                          <>
                            <th className="px-4 py-3 font-semibold">Note No</th>
                            <th className="px-4 py-3 font-semibold">Type</th>
                            <th className="px-4 py-3 font-semibold">Party</th>
                            <th className="px-4 py-3 font-semibold">Invoice</th>
                            <th className="px-4 py-3 font-semibold">Taxable</th>
                            <th className="px-4 py-3 font-semibold">Tax</th>
                            <th className="px-4 py-3 font-semibold">Total</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {reportsTab === "party" ? reportData.map((row) => (
                        <tr key={`${row.counterpartyType}-${row.counterpartyName}`} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-semibold">{row.counterpartyName}</td>
                          <td className="px-4 py-3">{row.counterpartyType}</td>
                          <td className="px-4 py-3">{row.noteCount}</td>
                          <td className="px-4 py-3 font-semibold">Rs {formatCurrency(row.totalAmount)}</td>
                        </tr>
                      )) : reportsTab === "outstanding" ? reportData.map((row) => (
                        <tr key={row.noteNumber} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-semibold">{row.noteNumber}</td>
                          <td className="px-4 py-3">{row.counterpartyName}</td>
                          <td className="px-4 py-3">{row.referenceInvoiceNumber || "-"}</td>
                          <td className="px-4 py-3 font-semibold">Rs {formatCurrency(row.balanceImpact)}</td>
                          <td className="px-4 py-3">{row.status}</td>
                        </tr>
                      )) : reportData.map((row) => (
                        <tr key={row.noteNumber || row._id} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-semibold">{row.noteNumber}</td>
                          <td className="px-4 py-3">{row.noteType}</td>
                          <td className="px-4 py-3">{row.counterpartyName}</td>
                          <td className="px-4 py-3">{row.referenceInvoiceNumber || "-"}</td>
                          <td className="px-4 py-3 font-semibold">Rs {formatCurrency(row.taxableAmount)}</td>
                          <td className="px-4 py-3 font-semibold">Rs {formatCurrency(row.taxAmount)}</td>
                          <td className="px-4 py-3 font-semibold">Rs {formatCurrency(row.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-10 text-center text-slate-500">No report rows found.</div>
                )}
              </div>
            </section>

            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionTitle title="Accounting Impact" />
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <div className="rounded-2xl bg-slate-50 px-4 py-3">Approved notes update invoice outstanding balances using the same balance fields used by the existing Accounts Monitor.</div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">Stock movement is posted on approval and reversed on cancel/delete so the inventory ledger stays in sync.</div>
                <div className="rounded-2xl bg-slate-50 px-4 py-3">Every create, update, approve, cancel, and delete action is logged into the note audit trail and shown in the PDF output.</div>
              </div>
            </section>
          </div>
        </section>
      </div>
    </div>
  );
}
